import React, { useState, useEffect } from 'react';
import {
  HiPlus, HiPencil, HiTrash, HiOutlineAcademicCap, HiOutlineUsers, HiOutlineLightBulb, HiOutlineGlobe, HiCheck,
  HiOutlineAnnotation, HiOutlineArchive, HiOutlineArrowCircleUp, HiOutlineBadgeCheck, HiOutlineBeaker,
  HiOutlineBell, HiOutlineBookOpen, HiOutlineBriefcase, HiOutlineCalendar, HiOutlineChartBar,
  HiOutlineChat, HiOutlineClipboardCheck, HiOutlineCloud, HiOutlineCode, HiOutlineCog, HiOutlineCollection,
  HiOutlineCurrencyDollar, HiOutlineDeviceMobile, HiOutlineDocumentText, HiOutlineEmojiHappy, HiOutlineExclamation,
  HiOutlineEye, HiOutlineFire, HiOutlineFlag, HiOutlineGift, HiOutlineHeart, HiOutlineKey, HiOutlineLibrary,
  HiOutlineLightningBolt, HiOutlineLocationMarker, HiOutlineLockClosed, HiOutlineMail, HiOutlineMap,
  HiOutlineMicrophone, HiOutlineMoon, HiOutlineMusicNote, HiOutlinePaperAirplane, HiOutlinePencilAlt,
  HiOutlinePhotograph, HiOutlinePresentationChartBar, HiOutlinePuzzle, HiOutlineRocket, HiOutlineScale,
  HiOutlineShieldCheck, HiOutlineSparkles, HiOutlineStar, HiOutlineSun, HiOutlineSupport, HiOutlineSwitchHorizontal,
  HiOutlineTerminal, HiOutlineThumbUp, HiOutlineTranslate, HiOutlineTrendingUp, HiOutlineTruck, HiOutlineUserGroup
} from 'react-icons/hi';
import { objectivesService } from '../../services/objectivesService';
import './styles/ObjectivesManagement.css';
import iconOptionsRaw from '../../assets/iconOptions.json';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import ExpandableText from './ExpandableText';

const iconComponentMap = {
  HiOutlineAcademicCap,
  HiOutlineUsers,
  HiOutlineLightBulb,
  HiOutlineGlobe,
  HiOutlineAnnotation,
  HiOutlineArchive,
  HiOutlineArrowCircleUp,
  HiOutlineBadgeCheck,
  HiOutlineBeaker,
  HiOutlineBell,
  HiOutlineBookOpen,
  HiOutlineBriefcase,
  HiOutlineCalendar,
  HiOutlineChartBar,
  HiOutlineChat,
  HiOutlineClipboardCheck,
  HiOutlineCloud,
  HiOutlineCode,
  HiOutlineCog,
  HiOutlineCollection,
  HiOutlineCurrencyDollar,
  HiOutlineDeviceMobile,
  HiOutlineDocumentText,
  HiOutlineEmojiHappy,
  HiOutlineExclamation,
  HiOutlineEye,
  HiOutlineFire,
  HiOutlineFlag,
  HiOutlineGift,
  HiOutlineHeart,
  HiOutlineKey,
  HiOutlineLibrary,
  HiOutlineLightningBolt,
  HiOutlineLocationMarker,
  HiOutlineLockClosed,
  HiOutlineMail,
  HiOutlineMap,
  HiOutlineMicrophone,
  HiOutlineMoon,
  HiOutlineMusicNote,
  HiOutlinePaperAirplane,
  HiOutlinePencilAlt,
  HiOutlinePhotograph,
  HiOutlinePresentationChartBar,
  HiOutlinePuzzle,
  HiOutlineScale,
  HiOutlineShieldCheck,
  HiOutlineSparkles,
  HiOutlineStar,
  HiOutlineSun,
  HiOutlineSupport,
  HiOutlineSwitchHorizontal,
  HiOutlineTerminal,
  HiOutlineThumbUp,
  HiOutlineTranslate,
  HiOutlineTrendingUp,
  HiOutlineTruck,
  HiOutlineUserGroup
};

// Compose iconOptions with component property
const iconOptions = iconOptionsRaw.map(opt => ({
  ...opt,
  component: iconComponentMap[opt.value] ? React.createElement(iconComponentMap[opt.value]) : <HiOutlineLightBulb />
}));

function ObjectivesManagement({ selectedEdition }) {
  const [objectivesData, setObjectivesData] = useState({
    objectivesHeader: {
      title: 'Nos Objectifs',
      subtitle: ''
    },
    objectives: []
  });
  
  const [editingObjective, setEditingObjective] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newObjective, setNewObjective] = useState({
    title: '',
    description: '',
    icon: 'HiOutlineLightBulb'
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDatabaseEmpty, setIsDatabaseEmpty] = useState(false);

  useEffect(() => {
    if (selectedEdition) {
      loadObjectivesData();
    }
  }, [selectedEdition]);

  useEffect(() => {
    if (originalData) {
      const hasChanges = JSON.stringify(originalData) !== JSON.stringify(objectivesData);
      setHasUnsavedChanges(hasChanges);
    }
  }, [objectivesData, originalData]);

  const loadObjectivesData = async () => {
    if (!selectedEdition) return;
    
    try {
      setLoading(true);
      console.log('Loading objectives data for edition:', selectedEdition);
      const data = await objectivesService.getObjectivesData(selectedEdition);
      
      if (!data || ((!data.objectivesHeader?.title || data.objectivesHeader.title === 'Nos Objectifs') && 
                    (!data.objectivesHeader?.subtitle || data.objectivesHeader.subtitle === '') &&
                    (!data.objectives || data.objectives.length === 0))) {
        console.log('No data found, setting defaults');
        setIsDatabaseEmpty(true);
        const defaultData = {
          objectivesHeader: {
            title: 'Nos Objectifs',
            subtitle: 'Découvrez les objectifs du congrès'
          },
          objectives: []
        };
        setObjectivesData(defaultData);
        setOriginalData(JSON.parse(JSON.stringify(defaultData)));
      } else {
        console.log('Data found:', data);
        setObjectivesData(data);
        setOriginalData(JSON.parse(JSON.stringify(data)));
        setIsDatabaseEmpty(false);
      }
    } catch (error) {
      console.error('Error loading objectives data:', error);
      setIsDatabaseEmpty(true);
      const defaultData = {
        objectivesHeader: {
          title: 'Nos Objectifs',
          subtitle: 'Découvrez les objectifs du congrès'
        },
        objectives: []
      };
      setObjectivesData(defaultData);
      setOriginalData(JSON.parse(JSON.stringify(defaultData)));
    } finally {
      setLoading(false);
    }
  };

  const handleEditObjective = (objective) => {
    setEditingObjective(objective);
  };

  const handleDeleteObjective = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cet objectif ?')) {
      try {
        await objectivesService.deleteObjective(id);
        setObjectivesData(prev => ({
          ...prev,
          objectives: prev.objectives.filter(objective => objective.id !== id)
        }));
        showSuccessMessage();
      } catch (error) {
        console.error('Error deleting objective:', error);
        alert('Erreur lors de la suppression de l\'objectif.');
      }
    }
  };

  const handleSaveObjective = async (updatedObjective) => {
    try {
      await objectivesService.updateObjective(updatedObjective.id, updatedObjective);
      setObjectivesData(prev => ({
        ...prev,
        objectives: prev.objectives.map(objective =>
          objective.id === updatedObjective.id ? { ...objective, ...updatedObjective } : objective
        )
      }));
      setEditingObjective(null);
      showSuccessMessage();
    } catch (error) {
      console.error('Error updating objective:', error);
      alert('Erreur lors de la modification de l\'objectif.');
    }
  };

  const handleAddObjective = async () => {
    try {
      if (isDatabaseEmpty) {
        // Add to local state with temporary ID
        const tempObjective = {
          ...newObjective,
          id: `temp_${Date.now()}`,
          displayOrder: objectivesData.objectives.length
        };
        setObjectivesData(prev => ({
          ...prev,
          objectives: [...prev.objectives, tempObjective]
        }));
        setHasUnsavedChanges(true);
      } else {
        const newObjectiveFromDb = await objectivesService.addObjective(newObjective, selectedEdition);
        const formattedObjective = {
          id: newObjectiveFromDb.id,
          title: newObjectiveFromDb.title,
          description: newObjectiveFromDb.description,
          icon: newObjectiveFromDb.icon,
          displayOrder: newObjectiveFromDb.display_order
        };
        
        setObjectivesData(prev => ({
          ...prev,
          objectives: [...prev.objectives, formattedObjective]
        }));
        showSuccessMessage();
      }
      
      setNewObjective({ title: '', description: '', icon: 'HiOutlineLightBulb' });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error adding objective:', error);
      alert('Erreur lors de l\'ajout de l\'objectif.');
    }
  };

  const getIconComponent = (iconName) => {
    const iconOption = iconOptions.find(option => option.value === iconName);
    return iconOption ? iconOption.component : <HiOutlineLightBulb />;
  };

  const handleSave = async () => {
    try {
      await objectivesService.updateObjectivesPageData(objectivesData, selectedEdition);
      
      // Save objectives if any with temp IDs
      for (const objective of objectivesData.objectives) {
        if (!objective.id || objective.id.toString().startsWith('temp_')) {
          await objectivesService.addObjective(objective, selectedEdition);
        }
      }
      
      setOriginalData(JSON.parse(JSON.stringify(objectivesData)));
      setHasUnsavedChanges(false);
      setIsDatabaseEmpty(false);
      showSuccessMessage();
      
      // Reload data to get proper IDs from database
      setTimeout(() => {
        loadObjectivesData();
      }, 1000);
    } catch (error) {
      console.error('Error saving objectives data:', error);
      alert('Erreur lors de la sauvegarde.');
    }
  };

  const handleReset = () => {
    if (originalData && window.confirm('Voulez-vous vraiment annuler toutes les modifications non sauvegardées ?')) {
      setObjectivesData(JSON.parse(JSON.stringify(originalData)));
      setHasUnsavedChanges(false);
    }
  };

  const handleUpdateObjectivesHeader = (field, value) => {
    setObjectivesData(prev => ({
      ...prev,
      objectivesHeader: { 
        ...prev.objectivesHeader, 
        [field]: value 
      }
    }));
  };

  const showSuccessMessage = () => {
    setShowSaveSuccess(true);
    setTimeout(() => setShowSaveSuccess(false), 3000);
  };

  if (!selectedEdition) {
    return <div className="admin-section">Please select an edition first</div>;
  }

  if (loading) {
    return <div className="admin-section">Loading...</div>;
  }

  return (
    <div className="admin-section">
      {/* Database Empty Warning */}
      {isDatabaseEmpty && (
        <div className="database-empty-warning">
          <div className="warning-content">
            <h3>Base de données vide pour l'édition {selectedEdition}</h3>
            <p>La base de données Objectives est vide pour cette édition. Remplissez les champs ci-dessous et cliquez sur "Sauvegarder" pour initialiser les données.</p>
          </div>
        </div>
      )}

      {/* Save/Reset Actions */}
      {hasUnsavedChanges && (
        <div className="save-actions-bar">
          <div className="save-message">
            <span>Vous avez des modifications non sauvegardées</span>
          </div>
          <div className="save-buttons">
            <button onClick={handleReset} className="btn-secondary">
              Annuler les modifications
            </button>
            <button onClick={handleSave} className="btn-primary save-btn">
              <HiCheck /> Sauvegarder
            </button>
          </div>
        </div>
      )}

      {/* Success Message */}
      {showSaveSuccess && (
        <div className="save-success-message">
          <HiCheck /> Modifications sauvegardées avec succès !
        </div>
      )}

      {/* Objectives Header Section */}
      <div className="section-header">
        <h3>En-tête des Objectifs</h3>
      </div>
      <div className="form-group">
        <label>Titre principal:</label>
        <input 
          type="text" 
          value={objectivesData.objectivesHeader.title}
          onChange={(e) => handleUpdateObjectivesHeader('title', e.target.value)}
          placeholder="Ex: Nos Objectifs"
        />
      </div>
      <div className="form-group">
        <label>Sous-titre:</label>
        <textarea 
          value={objectivesData.objectivesHeader.subtitle}
          onChange={(e) => handleUpdateObjectivesHeader('subtitle', e.target.value)}
          placeholder="Description des objectifs"
        />
      </div>

      {/* Objectives Management Section */}
      <div className="section-header">
        <h3>Gestion des Objectifs</h3>
        <button className="add-btn" onClick={() => setShowAddForm(true)}>
          <HiPlus /> Ajouter un objectif
        </button>
      </div>

      {/* Add Objective Form */}
      {showAddForm && (
        <div className="add-form">
          <div className="form-group">
            <label>Titre:</label>
            <input 
              type="text" 
              value={newObjective.title}
              onChange={(e) => setNewObjective({...newObjective, title: e.target.value})}
              placeholder="Titre de l'objectif"
              required
            />
          </div>
          <div className="form-group">
            <label>Description:</label>
            <textarea 
              value={newObjective.description}
              onChange={(e) => setNewObjective({...newObjective, description: e.target.value})}
              placeholder="Description détaillée de l'objectif"
              required
            />
          </div>
          <div className="form-group">
            <label>Icône:</label>
            <IconDropdown
              value={newObjective.icon}
              options={iconOptions}
              onChange={icon => setNewObjective({ ...newObjective, icon })}
            />
          </div>
          <div className="form-actions">
            <button onClick={handleAddObjective} className="btn-primary">
              <HiPlus /> Ajouter
            </button>
            <button onClick={() => setShowAddForm(false)} className="btn-secondary">
              Annuler
            </button>
          </div>
        </div>
      )}

      <div className="card">
        <DataTable 
          value={objectivesData.objectives} 
          className="p-datatable-sm"
          stripedRows
          showGridlines
          responsiveLayout="scroll"
          scrollable
          tableStyle={{ tableLayout: 'auto' }}
          emptyMessage="Aucun objectif trouvé."
        >
          <Column 
            field="title" 
            header="Titre" 
            body={(rowData) => (
              editingObjective?.id === rowData.id ? (
                <input 
                  type="text" 
                  value={editingObjective.title}
                  onChange={(e) => setEditingObjective({...editingObjective, title: e.target.value})}
                  className="p-inputtext p-component"
                  style={{ width: '100%' }}
                />
              ) : <strong>{rowData.title}</strong>
            )}
            style={{ minWidth: '150px' }}
          />
          <Column 
            field="description" 
            header="Description" 
            body={(rowData) => (
              editingObjective?.id === rowData.id ? (
                <textarea 
                  value={editingObjective.description}
                  onChange={(e) => setEditingObjective({...editingObjective, description: e.target.value})}
                  className="p-inputtext p-component"
                  style={{ width: '100%', minHeight: '60px' }}
                />
              ) : <ExpandableText text={rowData.description || ''} maxChars={100} />
            )}
            style={{ minWidth: '300px' }}
          />
          <Column 
            header="Icône" 
            body={(rowData) => (
              editingObjective?.id === rowData.id ? (
                <IconDropdown
                  value={editingObjective.icon}
                  options={iconOptions}
                  onChange={icon => setEditingObjective({ ...editingObjective, icon })}
                />
              ) : (
                <div className="flex justify-content-center">
                  {getIconComponent(rowData.icon)}
                </div>
              )
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
          <Column 
            header="Actions" 
            body={(rowData) => (
              editingObjective?.id === rowData.id ? (
                <div className="flex gap-2">
                  <Button icon="pi pi-check" onClick={() => handleSaveObjective(editingObjective)} rounded outlined severity="success" size="small" />
                  <Button icon="pi pi-times" onClick={() => setEditingObjective(null)} rounded outlined severity="secondary" size="small" />
                </div>
              ) : (
                <div className="flex gap-2">
                  <Button icon="pi pi-pencil" onClick={() => handleEditObjective(rowData)} rounded outlined severity="info" size="small" />
                  <Button icon="pi pi-trash" onClick={() => handleDeleteObjective(rowData.id)} rounded outlined severity="danger" size="small" />
                </div>
              )
            )}
            style={{ width: '1%', whiteSpace: 'nowrap' }}
          />
        </DataTable>
      </div>
    </div>
  );
}

function IconDropdown({ value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = options.find(opt => opt.value === value);

  return (
    <div
      className="custom-icon-dropdown"
      tabIndex={0}
      onBlur={() => setOpen(false)}
      style={{ position: 'relative', minWidth: 180 }}
    >
      <div
        className="custom-icon-dropdown-selected"
        onClick={() => setOpen(o => !o)}
      >
        <span className="icon-preview">{selected?.component}</span>
        <span className="icon-label">{selected?.label}</span>
        <span style={{ marginLeft: 'auto', fontSize: 14 }}>▼</span>
      </div>
      {open && (
        <div className="custom-icon-dropdown-list">
          {options.map(option => (
            <div
              key={option.value}
              className={`custom-icon-dropdown-item${option.value === value ? ' selected' : ''}`}
              onMouseDown={() => { onChange(option.value); setOpen(false); }}
            >
              <span className="icon-preview">{option.component}</span>
              <span className="icon-label">{option.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default ObjectivesManagement;
